import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import store from "src/page/store";
import { immediateToast } from "izitoast-react";
import { markSessionExpired, refreshSession } from "src/utils";
import { getConfig, AppConfig, isCookieAuthMode } from "src/config/Config";
import { logoutAction, setMfaEnrolmentRequired } from "src/page/auth/loginSlice";
import {
  showErrorAlert,
  errorCodeMessages,
  errorMapping,
  isMfaEnrolmentRequiredError,
  isSessionGoneError
} from "src/utils/errors";
import {
  COOKIE_API_BASE,
  GRAPHQL_PROXY_PATH,
  SESSION_AUTHORIZATION,
  WEB_REQUEST_MARKER,
  ensureSession
} from "src/network/authService";
import {
  trackEvent,
  API_ERROR_EVENTS,
  parseGraphQLOperation,
  trackGraphQLOperation,
  enrichEventProperties
} from "src/services/analytics";

declare module "axios" {
  export interface AxiosRequestConfig {
    // Opt-out for callers that render the failure inline and would otherwise show it twice.
    suppressErrorToast?: boolean;
    // Opt-in for callers that would rather render a partial read than blank the screen.
    allowPartialData?: boolean;
    // Opt-in for operations the authoriser may deny on ownership: a 401 that survives one
    // session check rejects the request instead of signing the user out.
    rejectOnDenial?: boolean;
  }
}

export type ClientOptions = {
  suppressErrorToast?: boolean;
  allowPartialData?: boolean;
  rejectOnDenial?: boolean;
};

const ACCESS_DENIED_FALLBACK = "You are not authorized to make this call.";

type GraphQLErrorResponse = { response?: { data?: { errors?: { message?: string }[] } } };

export const getAccessDeniedError = (error: GraphQLErrorResponse): Error =>
  new Error(error?.response?.data?.errors?.[0]?.message || ACCESS_DENIED_FALLBACK);

// Guards the partial-success path: a mutation that failed comes back as { data: { op: null } },
// which must still be treated as a failure, so the key existing is not enough.
const hasUsableData = (payload: unknown): boolean =>
  Boolean(payload && typeof payload === "object" && Object.values(payload).some((value) => value != null));

let apiInstance: AxiosInstance | null = null;

let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let isLoggedOut = false;

export function resetLoggedOutState() {
  isLoggedOut = false;
  isRefreshing = false;
  refreshPromise = null;
}

export function getApiClient(): AxiosInstance {
  if (apiInstance) {
    return apiInstance;
  }

  const config: AppConfig = getConfig();
  const cookieMode = isCookieAuthMode();

  const api = axios.create({
    baseURL: cookieMode ? COOKIE_API_BASE : config.API_URL,
    withCredentials: cookieMode,
    headers: {
      "Content-Type": "application/json",
      partner_id: config.PARTNER_ID
    }
  });

  api.interceptors.request.use(async (configuration: InternalAxiosRequestConfig) => {
    if (isLoggedOut) {
      return Promise.reject(new Error("Session expired. User logged out."));
    }
    if (cookieMode) {
      configuration.headers["Authorization"] = SESSION_AUTHORIZATION;
      configuration.headers["X-Requested-With"] = WEB_REQUEST_MARKER;
      return configuration;
    }
    try {
      const {
        auth: { cognitoIdToken }
      } = store.getState();

      if (cognitoIdToken) {
        configuration.headers = configuration.headers || {};
        configuration.headers["Authorization"] = `Bearer ${cognitoIdToken}`;
      }
    } catch (error) {
      console.error(error);
    }
    return configuration;
  });

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.config.url?.includes("/graphql") && response.config.data) {
        try {
          const requestData =
            typeof response.config.data === "string" ? JSON.parse(response.config.data) : response.config.data;

          if (requestData?.query) {
            const { methodName, methodType } = parseGraphQLOperation(requestData.query);
            const hasErrors = !!response.data.errors;

            trackGraphQLOperation({
              methodName,
              methodType,
              success: !hasErrors,
              requestPayload: requestData.variables,
              responseData: response.data,
              ...(hasErrors && {
                errorMessage: response.data.errors[0]?.message,
                errorType: response.data.errors[0]?.errorType
              })
            });
          }
        } catch (error) {
          console.error("Failed to track GraphQL operation:", error);
        }
      }

      if (response.data.errors) {
        const error = response.data.errors[0];
        const errorType = error.errorType;

        // Opt-in only. A GraphQL response can carry usable data alongside a populated errors
        // array; callers that ask for it get the partial read rather than a blank screen.
        // Everything else — and any response with no usable data — still throws below.
        if (response.config.allowPartialData && hasUsableData(response.data.data)) {
          console.error("GraphQL partial success — returning data", { errorType, message: error.message });
          return response.data;
        }

        if (!response.config.suppressErrorToast) {
          if (errorMapping[errorType]) {
            showErrorAlert(errorType, error.message);
          } else {
            const errorMessage = error.message;
            let messageObj: { message?: string; code?: string } | undefined;

            try {
              messageObj = JSON.parse(errorMessage);
            } catch (err) {
              console.log(err);
              messageObj = undefined;
            }

            immediateToast("error", {
              message: errorCodeMessages[messageObj?.code ?? ""] ?? messageObj?.message ?? errorMessage,
              timeout: 3000,
              position: "topCenter"
            });
          }
        }

        throw new Error(error.message);
      }
      return response.data;
    },
    async function (error: any) {
      if (error.config?.url?.includes("/graphql") && error.config.data) {
        try {
          const requestData = typeof error.config.data === "string" ? JSON.parse(error.config.data) : error.config.data;

          if (requestData?.query) {
            const { methodName, methodType } = parseGraphQLOperation(requestData.query);
            const errorMessage = error?.response?.data?.errors?.[0]?.message || error.message || "Unknown error";
            const errorType = error?.response?.data?.errors?.[0]?.errorType || error.constructor.name;

            trackGraphQLOperation({
              methodName,
              methodType,
              success: false,
              errorMessage,
              errorType,
              requestPayload: requestData.variables,
              responseData: error?.response?.data
            });
          }
        } catch (trackingError) {
          console.error("Failed to track GraphQL error:", trackingError);
        }
      }

      const originalRequest: InternalAxiosRequestConfig = error.config;
      const {
        auth: { cognitoRefreshToken }
      } = store.getState();
      const dispatch = store.dispatch;

      // Enrolment pending: the gate takes over the screen, so no logout and no toast.
      if (cookieMode && isMfaEnrolmentRequiredError(error)) {
        dispatch(setMfaEnrolmentRequired());
        return Promise.reject(error);
      }

      if (error.response?.status === 401) {
        if (isLoggedOut) {
          return Promise.reject(new Error("Session expired. User logged out."));
        }
        if (cookieMode) {
          // The proxy already retried an expired token once; one session check decides whether we are still signed in.
          const sessionRequest = originalRequest as InternalAxiosRequestConfig & { _sessionChecked?: boolean };

          if (!sessionRequest._sessionChecked) {
            sessionRequest._sessionChecked = true;
            trackEvent(
              API_ERROR_EVENTS.API_RETRY_ATTEMPTED,
              enrichEventProperties({
                endpoint: originalRequest.url || "unknown",
                method: originalRequest.method?.toUpperCase() || "unknown",
                retry_count: 1
              })
            );
            try {
              await ensureSession();
            } catch (sessionError) {
              // Only a 401 from the session check means we are signed out; anything else fails this request only.
              if (!isSessionGoneError(sessionError)) return Promise.reject(error);
              trackEvent(
                API_ERROR_EVENTS.TOKEN_REFRESH_FAILED,
                enrichEventProperties({
                  error_message: sessionError instanceof Error ? sessionError.message : "Session check failed"
                })
              );
              isLoggedOut = true;
              // A session already cleared by an explicit logout is not an expiry.
              if (store.getState().auth.session) markSessionExpired();
              dispatch(logoutAction());
              return Promise.reject(new Error("Session expired. User logged out."));
            }
            return api(sessionRequest);
          }
          if (originalRequest.rejectOnDenial) return Promise.reject(getAccessDeniedError(error));
          isLoggedOut = true;
          dispatch(logoutAction());
          return Promise.reject(new Error("Session expired. User logged out."));
        }
        const originalRequestWithRetry = originalRequest as InternalAxiosRequestConfig & { _retry?: number };

        if (!originalRequestWithRetry._retry) originalRequestWithRetry._retry = 0;

        if (originalRequestWithRetry.rejectOnDenial && originalRequestWithRetry._retry > 0) {
          return Promise.reject(getAccessDeniedError(error));
        }

        if (originalRequestWithRetry._retry < 4) {
          originalRequestWithRetry._retry += 1;

          // Track retry attempt
          const retryProperties = enrichEventProperties({
            endpoint: originalRequest.url || "unknown",
            method: originalRequest.method?.toUpperCase() || "unknown",
            retry_count: originalRequestWithRetry._retry
          });

          trackEvent(API_ERROR_EVENTS.API_RETRY_ATTEMPTED, retryProperties);

          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = (async () => {
              try {
                const session = await refreshSession(cognitoRefreshToken ?? undefined);

                isRefreshing = false;
                refreshPromise = null;
                return session;
              } catch (err) {
                isRefreshing = false;
                refreshPromise = null;
                isLoggedOut = true;
                // Track token refresh failure
                const refreshErrorProperties = enrichEventProperties({
                  error_message: err instanceof Error ? err.message : "Token refresh failed"
                });

                trackEvent(API_ERROR_EVENTS.TOKEN_REFRESH_FAILED, refreshErrorProperties);
                dispatch(logoutAction());
                return Promise.reject(new Error("Session expired. User logged out."));
              }
            })();
          }

          try {
            const session = await refreshPromise;

            if (originalRequestWithRetry.headers) {
              originalRequestWithRetry.headers["Authorization"] =
                `Bearer ${(session as unknown as { idToken: string }).idToken}`;
            }
            return api(originalRequestWithRetry);
          } catch (err) {
            return Promise.reject(new Error("Session expired. User logged out."));
          }
        } else {
          isLoggedOut = true;
          dispatch(logoutAction());
          return Promise.reject(new Error("Session expired. User logged out."));
        }
      } else {
        if (!originalRequest?.suppressErrorToast) {
          immediateToast("error", {
            message: error.message || "An unexpected error occurred",
            timeout: 3000,
            position: "topCenter"
          });
        }
        return Promise.reject(error);
      }
    }
  );

  apiInstance = api;
  return apiInstance;
}

const client = (data: { query: string; variables?: any }, options?: ClientOptions) => {
  return getApiClient().post(GRAPHQL_PROXY_PATH, data, options);
};

export default client;
